import { PayloadAction, createSlice } from '@reduxjs/toolkit'
import {
  LocationType,
  RouteMasterLogType,
  RouteWaveConstantOptionType,
  extractHHMM,
} from 'features/Midmile/service/routeAndSchedule'
import {
  SLICE_NAME,
  addRouteMaster,
  fetchRouteConfig,
  fetchRouteMasterDetail,
  fetchRouteMasterLogs,
  updateRouteMaster,
} from './routeAndScheduleAddEditThunk'

export type PageType = 'ADD' | 'EDIT'

export type FormDataType = {
  name: string
  active: boolean
  distanceType: string
  tripType: string
  rit: string
  wave: string
  departureTime: string
}

export type OriginFormType = {
  type: LocationType[number] | ''
  locationID: number | null
  locationName: string
}

export type DestinationFormType = {
  id: string
  type: LocationType[number] | ''
  locationID: number | null
  locationName: string
  estimatedArrivalTime: string
  remoteId: number
  status: 'CREATE' | 'UPDATE'
}

export type SetFormDataPayloadType = {
  key: keyof FormDataType
  value: FormDataType[keyof FormDataType]
}

export type SetOriginPayloadType = {
  key: keyof OriginFormType
  value: OriginFormType[keyof OriginFormType]
}

export type SetDestinationPayloadType = {
  id: string
  key: keyof DestinationFormType
  value: DestinationFormType[keyof DestinationFormType]
}

export interface RouteAndScheduleAddEditStateInterface {
  isLoading: boolean
  pageType: PageType
  formData: FormDataType
  origin: OriginFormType
  destinations: DestinationFormType[]
  deletedDestinationIds: number[]
  distanceList: RouteWaveConstantOptionType[]
  tripTypeList: RouteWaveConstantOptionType[]
  ritList: RouteWaveConstantOptionType[]
  waveList: RouteWaveConstantOptionType[]
  locationTypeList: LocationType[number][]
  logs: RouteMasterLogType[]
}

const LOCATION_TYPE_LIST: LocationType[number][] = [
  'HUB',
  'MEGAHUB',
  'OFFICE',
  'VENDING MACHINE',
  'VENDOR',
  'VIRTUAL',
  'WAREHOUSE',
]

const buildOrigin = (): OriginFormType => ({
  type: '',
  locationID: null,
  locationName: '',
})

const buildDestination = (): DestinationFormType => ({
  id: window.crypto.randomUUID(),
  type: '',
  locationID: null,
  locationName: '',
  estimatedArrivalTime: '',
  remoteId: 0,
  status: 'CREATE',
})

const initialState: RouteAndScheduleAddEditStateInterface = {
  isLoading: false,
  pageType: 'ADD',
  formData: {
    name: '',
    active: false,
    distanceType: '',
    tripType: '',
    rit: '',
    wave: '',
    departureTime: '',
  },
  origin: buildOrigin(),
  destinations: [buildDestination()],
  deletedDestinationIds: [],
  distanceList: [],
  tripTypeList: [],
  ritList: [],
  waveList: [],
  locationTypeList: LOCATION_TYPE_LIST,
  logs: [],
}

const slice = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    resetState: () => initialState,
    setPageType: (state, { payload }: PayloadAction<PageType>) => {
      state.pageType = payload
    },
    setFormData: (state, { payload }: PayloadAction<SetFormDataPayloadType>) => {
      state.formData[payload.key] = payload.value as never
    },
    setOriginForm: (state, { payload }: PayloadAction<SetOriginPayloadType>) => {
      state.origin[payload.key] = payload.value as never
    },
    setDestinationForm: (state, { payload }: PayloadAction<SetDestinationPayloadType>) => {
      const idx = state.destinations.findIndex((d) => d.id === payload.id)
      if (idx > -1) {
        state.destinations[idx][payload.key] = payload.value as never
      }
    },
    addDestinationForm: (state) => {
      state.destinations.push(buildDestination())
    },
    deleteDestinationForm: (state, { payload }: PayloadAction<string>) => {
      const target = state.destinations.find((d) => d.id === payload)
      if (target && target.status === 'UPDATE' && target.remoteId > 0) {
        state.deletedDestinationIds.push(target.remoteId)
      }
      state.destinations = state.destinations.filter((d) => d.id !== payload)
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchRouteConfig.fulfilled, (state, { payload }) => {
        state.distanceList = payload.distance_types
        state.tripTypeList = payload.trip_types
        state.ritList = payload.rit
        state.waveList = payload.wave
      })
      .addCase(addRouteMaster.pending, (state) => {
        state.isLoading = true
      })
      .addCase(addRouteMaster.fulfilled, (state) => {
        state.isLoading = false
      })
      .addCase(addRouteMaster.rejected, (state) => {
        state.isLoading = false
      })
      .addCase(updateRouteMaster.pending, (state) => {
        state.isLoading = true
      })
      .addCase(updateRouteMaster.fulfilled, (state) => {
        state.isLoading = false
      })
      .addCase(updateRouteMaster.rejected, (state) => {
        state.isLoading = false
      })
      .addCase(fetchRouteMasterDetail.fulfilled, (state, { payload }) => {
        state.formData = {
          name: payload.name,
          active: payload.active,
          distanceType: payload.distance_type,
          tripType: payload.trip_type,
          rit: `${payload.rit}`,
          wave: `${payload.wave}`,
          departureTime: extractHHMM(payload.departure_time),
        }
        state.origin = {
          type: (payload.location.type as LocationType[number]) || '',
          locationID: payload.location.id,
          locationName: payload.location.name,
        }
        state.destinations = payload.route_wave_destinations.map((d) => ({
          id: window.crypto.randomUUID(),
          type: (d.location.type as LocationType[number]) || '',
          locationID: d.location.id,
          locationName: d.location.name,
          estimatedArrivalTime: extractHHMM(d.estimated_arrival_time),
          remoteId: d.id,
          status: 'UPDATE',
        }))
        state.deletedDestinationIds = []
      })
      .addCase(fetchRouteMasterLogs.fulfilled, (state, { payload }) => {
        state.logs = payload
      })
  },
})

export const {
  resetState,
  setPageType,
  setFormData,
  setOriginForm,
  setDestinationForm,
  addDestinationForm,
  deleteDestinationForm,
} = slice.actions
export default slice.reducer
